import React from 'react';

import { Divider } from '@mui/material';

import {
  Listing,
  PropertyAdvertSortOptionID,
} from '@klokhuis/core/ApiClient/Klokhuis';
import { PageLayout } from '@klokhuis/core/PageLayout';
import { useFilterSelectors } from '@klokhuis/modules/Properties/context/hooks/useFilterSelectors';
import { useListingSelectors } from '@klokhuis/modules/Properties/context/hooks/useListingSelectors';
import { useSortedListings } from '@klokhuis/modules/Properties/hooks/useSortedListings';
import { ListingsContext } from '@klokhuis/modules/Properties/context/ListingsContext';
import { CommonStyles } from '@klokhuis/styles/CommonStyles';

import { GridGenerator } from '../GridGenerator/GridGenerator';
import { SortListings } from '../SortListings/SortListings';
import SortListingsSkeleton from '../SortListings/SortListingsSkeleton';

import { FeaturedListings } from './FeaturedListing/FeaturedListings';
import styles from './GridView.module.css';
import { NearbyListings } from './NearbyListings/NearbyListings';
import NoListingsFallback from './NoListingsFallback/NoListingsFallback';
import Pagination from './Pagination/Pagination';

export interface GridViewProps {
  className?: string;
}

const GridView = ({ className }: GridViewProps) => {
  const listings = useListingSelectors().getListings();
  const { listingsState } = ListingsContext.useContext();

  const sortOptionId =
    listingsState.filterResult.filters.listingSortOptionId ||
    PropertyAdvertSortOptionID.NewestFirst;
  const sortedListings: any = useSortedListings(listings, sortOptionId);

  const isLoading = useListingSelectors().isLoading();
  const topAdListings = useListingSelectors().getFeaturedListings();
  const nearbyListings = useListingSelectors().getNearbyListings();
  const isSellerPage = useFilterSelectors().isSellerPage();
  const sellerDisplayName = useFilterSelectors().getSellerDisplayName();
  const beforeFeaturedListing = sortedListings
    ? sortedListings?.slice(0, 6)
    : [];
  const afterFeaturedListing =
    sortedListings && sortedListings?.length > 6
      ? sortedListings?.slice(6)
      : [];
  const isArrayNotEmpty = (array: Listing[] | undefined) =>
    Array.isArray(array) && array.length > 0;

  return (
    <PageLayout.SectionContainer
      className={className}
      containerClassName={styles.root}
    >
      {isLoading ? <SortListingsSkeleton /> : <SortListings />}
      {beforeFeaturedListing.length > 0 && (
        <React.Fragment>
          <GridGenerator listings={beforeFeaturedListing} />
          <Divider className={CommonStyles.margin_bottom_3} />
        </React.Fragment>
      )}
      <FeaturedListings />

      {afterFeaturedListing.length > 0 && (
        <GridGenerator listings={afterFeaturedListing} />
      )}
      <NearbyListings />

      {!isArrayNotEmpty(sortedListings) &&
        !isArrayNotEmpty(topAdListings) &&
        !isArrayNotEmpty(nearbyListings) && <NoListingsFallback />}
      <Pagination />
    </PageLayout.SectionContainer>
  );
};

export default GridView;
